import { useEffect, useRef, useState } from 'react';
import { KIND_ICON, fmtSize } from '../../lib/chatFiles';

// How long a touch has to be held before it counts as a long-press rather than
// a tap that opens the file. 500ms is the platform convention.
const LONG_PRESS_MS = 500;

// One message bubble, shared by the staff cockpit and the client portal.
//
// Both sides render exactly the same thing — quoted reply, attachments, body,
// timestamp — so they live here once. What differs is only which side the
// bubble sits on and how a file URL is fetched, both of which are props.

// Images render inline; everything else is a labelled row you click to open.
// The URL is fetched on demand rather than up front: presigned S3 links expire
// in five minutes, so minting one for every file in a long transcript would
// hand back a page of dead links.
function Attachment({ file, onOpen, onFile }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [menu, setMenu] = useState(false);
  const holdTimer = useRef(null);
  const heldRef = useRef(false);
  const wrapRef = useRef(null);

  // `onFile` is passed ONLY by the staff page. The portal renders the same
  // component without it, so the whole menu — and every reference to the firm's
  // tasks and filing periods behind it — simply does not exist client-side.
  const canFile = typeof onFile === 'function';

  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setMenu(false); };
    const esc = (e) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [menu]);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  // Long-press on touch, right-click on desktop, and a visible ⋯ button so the
  // option is discoverable without knowing either gesture.
  const startHold = () => {
    if (!canFile) return;
    heldRef.current = false;
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => { heldRef.current = true; setMenu(true); }, LONG_PRESS_MS);
  };
  const endHold = () => clearTimeout(holdTimer.current);

  const open = async () => {
    // A long-press that opened the menu must not also open the file when the
    // finger lifts.
    if (heldRef.current) { heldRef.current = false; return; }
    setBusy(true);
    try {
      const url = await onOpen(file);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setBusy(false);
    }
  };

  const loadPreview = async () => {
    if (preview || busy || failed) return;
    setBusy(true);
    try {
      const url = await onOpen(file);
      if (url) setPreview(url); else setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  // Shared by both renderings below, so long-press / right-click / ⋯ behave
  // identically whether the attachment is an image or a file row.
  const holdProps = canFile
    ? {
      onTouchStart: startHold,
      onTouchEnd: endHold,
      onTouchMove: endHold,
      onTouchCancel: endHold,
      onContextMenu: (e) => { e.preventDefault(); setMenu(true); },
    }
    : {};

  const menuNode = canFile && menu && (
    <div className="chat-att-menu" role="menu">
      <button type="button" role="menuitem" onClick={() => { setMenu(false); onFile(file); }}>
        📁 Attach to client records…
      </button>
      <button type="button" role="menuitem" onClick={() => { setMenu(false); heldRef.current = false; open(); }}>
        ↗ Open file
      </button>
    </div>
  );

  const menuButton = canFile && (
    <button
      type="button"
      className="chat-att-more"
      onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }}
      aria-haspopup="menu"
      aria-expanded={menu}
      aria-label={`Options for ${file.fileName}`}
      title="Options"
    >
      ⋯
    </button>
  );

  if (file.kind === 'image' && !failed) {
    return (
      <div className="chat-att-wrap" ref={wrapRef}>
        <button
          type="button"
          className="chat-att-image"
          onClick={preview ? open : loadPreview}
          {...holdProps}
        >
          {preview ? (
            <img src={preview} alt={file.fileName} onError={() => setFailed(true)} />
          ) : (
            <span className="chat-att-image-load">{busy ? 'Loading…' : '🖼️ Show image'}</span>
          )}
          <span className="chat-att-caption">{file.fileName}</span>
        </button>
        {menuButton}
        {menuNode}
      </div>
    );
  }

  return (
    <div className="chat-att-wrap" ref={wrapRef}>
      <button type="button" className="chat-att-file" onClick={open} disabled={busy} {...holdProps}>
        <span className="chat-att-icon" aria-hidden>{KIND_ICON[file.kind] || '📎'}</span>
        <span className="chat-att-meta">
          <span className="chat-att-name">{file.fileName}</span>
          <span className="chat-att-size">{busy ? 'Opening…' : fmtSize(file.sizeBytes)}</span>
        </span>
      </button>
      {menuButton}
      {menuNode}
    </div>
  );
}

function ChatMessageBubble({ message, mine, showSender, onReply, onOpenAttachment, onJumpToReply, onFileAttachment }) {
  const files = message.attachments || [];
  const quoted = message.replyTo;
  // A bare file needs no bubble around it — the file card is already the object
  // on screen. A sender name does NOT disqualify it: the name renders fine
  // above a bare card, and requiring its absence meant every incoming
  // attachment (which always names the client contact) kept the redundant
  // panel this was written to remove.
  const mediaOnly = files.length > 0 && !message.body && !message.isDeleted && !quoted;

  return (
    <div className={`chat-msg chat-msg-${mine ? 'out' : 'in'}`}>
      <div className={`chat-bubble${mediaOnly ? ' chat-bubble-media' : ''}`}>
        {quoted && (
          <button
            type="button"
            className="chat-quote"
            onClick={() => onJumpToReply?.(quoted.id)}
            title="Go to the quoted message"
          >
            <span className="chat-quote-who">{quoted.senderName}</span>
            <span className="chat-quote-text">
              {quoted.preview || (quoted.hasAttachment ? '📎 Attachment' : 'Message removed')}
            </span>
          </button>
        )}

        {showSender && <span className="chat-msg-who">{message.senderName}</span>}

        {files.length > 0 && (
          <div className="chat-att-list">
            {files.map((f) => (
              <Attachment key={f.documentId} file={f} onOpen={onOpenAttachment} onFile={onFileAttachment} />
            ))}
          </div>
        )}

        {/* `-bare` when there is no text: the timestamp then has nothing to
            reserve space inside, so it drops back into the flow instead of
            being pinned over whatever the last element happens to be (it was
            landing on top of an image's filename). */}
        <div className={`chat-msg-line${message.body || message.isDeleted ? '' : ' chat-msg-line-bare'}`}>
          <span className="chat-msg-time">
            {message.sentAt ? new Date(message.sentAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
          </span>
          {(message.body || message.isDeleted) && (
            <span className={`chat-msg-body${message.isDeleted ? ' chat-msg-gone' : ''}`}>
              {message.isDeleted ? 'Message removed' : message.body}
            </span>
          )}
        </div>
      </div>

      {onReply && !message.isDeleted && (
        <button
          type="button"
          className="chat-reply-btn"
          onClick={() => onReply(message)}
          aria-label={`Reply to ${message.senderName}`}
          title="Reply"
        >
          ↩
        </button>
      )}
    </div>
  );
}

export default ChatMessageBubble;
